import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { createUseStyles } from "react-jss";
import classnames from "classnames";
import { ErrorTooltip } from "@ko2-consulting/leaf";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Spinner from "react-bootstrap/esm/Spinner";
import sectionsSlice from "../redux/sections/slice";
import { useSections } from "../hooks";

const useStyles = createUseStyles({
  body: {
    padding: "1rem 4.5rem 4.5rem 4.5rem",
  },
  input: {
    backgroundImage: "url('') !important",
  },
  errorTooltip: {
    top: "2.2rem",
    right: "0.9rem !important",
  },
  addQuestionButton: {
    width: "7.5rem",
  },
});

interface Props {
  onHide: any;
}

const normalizeQuestionTitle = (title: string) => {
  if (title === "QuestionBreak") {
    return "Question Break";
  }

  if (title === "SectionBreak") {
    return "Section Break";
  }

  if (title === "QuestionTwoUnitsNumber") {
    return "Two Units Number";
  }

  if (title === "QuestionUnitNumber") {
    return "Unit Number";
  }

  if (title === "QuestionPresetUnitNumber") {
    return "Preset Unit Number";
  }

  return title.replace("Question", "").replace("Section", "Section ");
};

const CreateQuestionModal = ({ onHide }: Props) => {
  const classes = useStyles();
  const [formValidated, setFormValidated] = useState(false);
  const [selectedQuestionType, setSelectedQuestionType] = useState<string>("");
  const [selectedQuestionSet, setSelectedQuestionSet] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const sections = useSections();
  const startersSection = sections.data.find(
    (section: any) => section.name === "starters"
  );
  const questionTypes = startersSection.questions
    .map((question: any) => ({
      id: question.id,
      label: normalizeQuestionTitle(question.title),
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label));
  const questionSets = sections.data
    .filter((section: any) => section.name !== "starters")
    .map((section: any) => ({
      id: section.id,
      label: section.title,
    }));
  const dispatch = useDispatch();
  const selectedSectionQuestions = sections.data.find(
    (section: any) => section.id === selectedQuestionSet
  )?.questions;

  useEffect(() => {
    if (isLoading) {
      onHide({
        sectionId: selectedQuestionSet,
      });
    }
  }, [selectedSectionQuestions]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setFormValidated(true);
      return;
    }

    setFormValidated(true);
    setIsLoading(true);

    dispatch(
      sectionsSlice.actions.copyQuestionFetch({
        sectionId: selectedQuestionSet,
        questionId: selectedQuestionType,
      })
    );
  };

  return (
    <Modal show onHide={() => onHide()} centered size="lg" backdrop="static">
      <Modal.Header className="border-0" closeButton />
      <Modal.Body
        className={classnames(
          "d-flex flex-column justify-content-center align-items-center text-justify",
          classes.body
        )}
      >
        <h2 className="mb-5">Add a new Question</h2>

        <Form
          noValidate
          className="w-75"
          onSubmit={handleSubmit}
          validated={formValidated}
        >
          <Form.Group as={Row}>
            <Col xs={12}>
              <Form.Control
                className={classes.input}
                as="select"
                value={selectedQuestionType}
                onChange={(event: any) =>
                  setSelectedQuestionType(event.target.value)
                }
                required
              >
                <option disabled value="">
                  Question Type
                </option>
                {questionTypes.map((questionType: any) => (
                  <option key={questionType.id} value={questionType.id}>
                    {questionType.label}
                  </option>
                ))}
              </Form.Control>
              <ErrorTooltip
                className={classes.errorTooltip}
                message="Please select a question type."
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mt-4">
            <Col xs={12}>
              <Form.Control
                className={classes.input}
                as="select"
                value={selectedQuestionSet}
                onChange={(event: any) =>
                  setSelectedQuestionSet(event.target.value)
                }
                required
              >
                <option disabled value="">
                  Question Set
                </option>
                {questionSets.map((questionSet: any) => (
                  <option key={questionSet.id} value={questionSet.id}>
                    {questionSet.label}
                  </option>
                ))}
              </Form.Control>
              <ErrorTooltip
                className={classes.errorTooltip}
                message="Please select a question set."
              />
            </Col>
          </Form.Group>

          <Row className="w-100">
            <Col
              xs={12}
              className="d-flex align-items-center justify-content-center mt-5"
            >
              <Button
                className="ml-2 mr-2"
                variant="outline-primary"
                onClick={() => onHide()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={classnames(classes.addQuestionButton, "ml-2 mr-2")}
                variant="outline-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner animation="border" variant="dark" size="sm" />
                ) : (
                  "Add Question"
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateQuestionModal;
